import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ConfirmModalMC from '../../../components/ConfirmModalMC';
import useConfirmModal from '../../../components/useConfirmModal';
import useCustomerDocuments from './hooks/useCustomerDocuments';
import DocumentsDropzone from './components/DocumentsDropzone';
import DocumentsList from './components/DocumentsList';

const CustomerDocumentsModuleMC = ({ customerId, source = 'crm', compact = false }) => {
  const { t } = useTranslation(['CustomersScreenMC', 'InboxMC']);
  const { retailer_info: retailerInfo } = useSelector((state) => state.retailerUsersReducer) || {};
  const documentsEnabled = retailerInfo?.allows_customer_documents === true;
  const retailerScope = useMemo(() => {
    if (retailerInfo?.id) return `retailer:${retailerInfo.id}`;
    if (typeof ENV !== 'undefined' && ENV?.SLUG) return `slug:${ENV.SLUG}`;
    return 'retailer:unknown';
  }, [retailerInfo?.id]);
  const {
    documents,
    loading,
    uploading,
    deletingDocumentId,
    deleteDocument,
    onDrop
  } = useCustomerDocuments({
    customerId,
    source,
    retailerScope,
    documentsEnabled,
    t
  });

  const { confirm, modalProps } = useConfirmModal();

  const handleDelete = useCallback(async (documentId) => {
    const confirmed = await confirm({
      title: t('customerDocuments.confirmDeleteTitle', { defaultValue: 'Eliminar documento' }),
      description: t('customerDocuments.confirmDelete', { defaultValue: '¿Seguro que deseas eliminar este documento?' }),
      confirmLabel: t('customerDocuments.delete', { defaultValue: 'Eliminar' }),
      cancelLabel: t('common.cancel', { defaultValue: 'Cancelar' }),
      variant: 'DANGER'
    });

    if (!confirmed) return;
    await deleteDocument(documentId);
  }, [confirm, deleteDocument, t]);

  if (!documentsEnabled) {
    return (
      <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-yellow-200 tw-bg-yellow-50 tw-p-3 tw-text-xs tw-text-yellow-800">
        {t('customerDocuments.upgradeMessage', {
          defaultValue: 'Esta funcionalidad no está disponible para tu plan actual. Actualiza tu plan y empieza a guardar documentos importantes de tus clientes.'
        })}
      </div>
    );
  }

  return (
    <>
      <div className="tw-space-y-3">
        <DocumentsDropzone
          onDrop={onDrop}
          uploading={uploading}
          disabled={uploading || !documentsEnabled}
          t={t}
        />
        <DocumentsList
          documents={documents}
          loading={loading}
          source={source}
          deletingDocumentId={deletingDocumentId}
          onDelete={handleDelete}
          compact={compact}
          t={t}
        />
      </div>
      <ConfirmModalMC {...modalProps} />
    </>
  );
};

export default CustomerDocumentsModuleMC;
